import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { FiFilter } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer/Footer";
import CustomHits from "../../components/Reports/CustomHits";
import ReportHeader from "../../components/Reports/ReportHeader";
import ReportPagination from "../../components/Reports/ReportPagination";
import ReportsService from "../../services/reports-service";

const toFacetItems = (items = []) =>
  items.map((item) => ({
    label: item.value,
    value: item.value,
    count: item.count,
  }));

const REPORT_FILTER_ORDER = ["Sri-LR", "NLR"];
const REPORT_STATE_KEY = "reportsState";
const PENDING_REPORT_STATE_KEY = "pendingReportState";
const REPORT_SEARCH_DEBOUNCE_MS = 400;

const formatVolumeLabel = (volume) => String(volume).replace(/^Volume\s*/i, "");

const mergeReportFacetItems = (currentItems = [], cachedItems = []) => {
  const currentMap = new Map(currentItems.map((item) => [item.value, item]));
  const cachedMap = new Map(cachedItems.map((item) => [item.value, item]));
  const values = [
    ...REPORT_FILTER_ORDER,
    ...currentItems.map((item) => item.value),
    ...cachedItems.map((item) => item.value),
  ];

  return [...new Set(values)].map((value) => {
    const item = currentMap.get(value) || cachedMap.get(value);
    return {
      label: item?.label || value,
      value,
      count: item?.count || 0,
    };
  });
};

const getStoredReportState = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const normalizeReportState = (state = {}) => {
  const page = Number.parseInt(state.page || "1", 10);

  return {
    page: Number.isNaN(page) || page < 1 ? 1 : page,
    searchQuery: state.searchQuery || "",
    selectedReport: state.selectedReport || "",
    selectedReportYear: state.selectedReportYear || "",
    selectedJudgmentYear: state.selectedJudgmentYear || "",
    selectedVolume: state.selectedVolume || "",
    reportFacetItems: Array.isArray(state.reportFacetItems) ? state.reportFacetItems : [],
  };
};

function ActiveReportFilters({
  selectedReport,
  selectedReportYear,
  selectedJudgmentYear,
  selectedVolume,
  setSelectedReport,
  setSelectedReportYear,
  setSelectedJudgmentYear,
  setSelectedVolume,
  setCurrentPage,
}) {
  const activeFilters = [
    selectedReport
      ? {
          key: `report-${selectedReport}`,
          label: selectedReport,
          onRemove: () => {
            setSelectedReport("");
            setSelectedReportYear("");
            setSelectedJudgmentYear("");
            setSelectedVolume("");
            setCurrentPage(1);
          },
        }
      : null,
    selectedReportYear
      ? {
          key: `report-year-${selectedReportYear}`,
          label: `${selectedReportYear} (Sri LR)`,
          onRemove: () => {
            setSelectedReportYear("");
            setCurrentPage(1);
          },
        }
      : null,
    selectedJudgmentYear
      ? {
          key: `judgment-year-${selectedJudgmentYear}`,
          label: `${selectedJudgmentYear} (judgment)`,
          onRemove: () => {
            setSelectedJudgmentYear("");
            setCurrentPage(1);
          },
        }
      : null,
    selectedVolume
      ? {
          key: `volume-${selectedVolume}`,
          label: formatVolumeLabel(selectedVolume),
          onRemove: () => {
            setSelectedVolume("");
            setCurrentPage(1);
          },
        }
      : null,
  ].filter(Boolean);

  const hasFilters = activeFilters.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2 px-2 mb-4">
      {activeFilters.map((filter) => (
        <span
          key={filter.key}
          className="rounded-full py-0.5 px-2 text-13 cursor-pointer border border-pl-border bg-pl-bg-elevated text-pl-text-primary"
          onClick={filter.onRemove}
        >
          {filter.label} ✕
        </span>
      ))}

      {hasFilters && (
        <button
          onClick={() => {
            setSelectedReport("");
            setSelectedReportYear("");
            setSelectedJudgmentYear("");
            setSelectedVolume("");
            setCurrentPage(1);
          }}
          className="rounded-full py-0.5 px-2 text-13 border border-pl-border bg-pl-bg-subtle text-pl-text-primary"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

function ReportFiltersPanel({
  isMobile = false,
  facets,
  selectedReport,
  selectedReportYear,
  selectedJudgmentYear,
  selectedVolume,
  reportFacetItems,
  setSelectedReport,
  setSelectedReportYear,
  setSelectedJudgmentYear,
  setSelectedVolume,
  setCurrentPage,
}) {
  const [expandedPanel, setExpandedPanel] = useState("series");
  const [reportYearLimit, setReportYearLimit] = useState(10);
  const [reportYearSearch, setReportYearSearch] = useState("");
  const [judgmentYearLimit, setJudgmentYearLimit] = useState(10);
  const [judgmentYearSearch, setJudgmentYearSearch] = useState("");

  useEffect(() => {
    if (selectedReport === "Sri-LR") {
      setExpandedPanel("report_year");
      return;
    }

    if (selectedReport === "NLR") {
      setExpandedPanel("volume");
      return;
    }

    setExpandedPanel("series");
  }, [selectedReport]);

  const sortYearItemsDesc = (items) =>
    items.sort((a, b) => {
      const yearA = Number.parseInt(String(a.label), 10);
      const yearB = Number.parseInt(String(b.label), 10);

      if (Number.isNaN(yearA)) {
        return 1;
      }
      if (Number.isNaN(yearB)) {
        return -1;
      }
      return yearB - yearA;
    });

  const reportItems = mergeReportFacetItems(toFacetItems(facets.report), reportFacetItems);
  const reportYearItems = sortYearItemsDesc(toFacetItems(facets.report_year));
  const judgmentYearItems = sortYearItemsDesc(toFacetItems(facets.decision_year));
  const volumeItems = toFacetItems(facets.volume_num);

  const togglePanel = (id) => {
    setExpandedPanel(expandedPanel === id ? null : id);
  };

  const filteredReportYearItems = reportYearItems.filter((item) =>
    String(item.label).includes(reportYearSearch.trim())
  );
  const filteredJudgmentYearItems = judgmentYearItems.filter((item) =>
    String(item.label).includes(judgmentYearSearch.trim())
  );

  const sortedVolumeItems = [...volumeItems].sort((a, b) => {
    const numA = Number.parseInt(String(a.label).replace(/volume\s*/i, "").trim(), 10);
    const numB = Number.parseInt(String(b.label).replace(/volume\s*/i, "").trim(), 10);

    if (Number.isNaN(numA)) {
      return 1;
    }
    if (Number.isNaN(numB)) {
      return -1;
    }
    return numA - numB;
  });

  const commonClassNames = {
    count: "bg-[#9d9d9d30] rounded-full text-10 font-normal px-2 ml-10 dark:bg-[#434343]",
    showMore: "text-11 mt-2 text-left cursor-pointer text-pl-text-tertiary hover:text-pl-accent",
  };

  const renderYearFilterContent = ({
    items,
    filteredItems,
    search,
    setSearch,
    limit,
    setLimit,
    selectedValue,
    onSelect,
    helperText,
  }) => (
    <div className="filter-search flex flex-col justify-center">
      {!isMobile && helperText && (
        <p className="mb-2 text-11 leading-4 text-pl-text-tertiary">{helperText}</p>
      )}
      <div className="flex max-w-[200px]">
        <input
          type="text"
          placeholder="Search year"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setLimit(10);
          }}
          className="mb-[10px] h-[1.8rem] w-full rounded-[1rem] border-0 bg-[#dcdcdc83] px-[0.8rem] py-0 text-[0.8rem] text-pl-text-primary outline-none focus:outline-none dark:bg-[#62626258] dark:text-[rgb(212,212,212)]"
        />
      </div>

      <div className="pr-1">
        {(search.trim() ? filteredItems : filteredItems.slice(0, limit)).map((item) => (
          <label
            key={item.value}
            className={`flex items-center text-13 mb-1 px-2 py-1 rounded ${
              item.value === selectedValue ? "dark:bg-[#333]" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={item.value === selectedValue}
              onChange={() => {
                const next = item.value === selectedValue ? "" : item.value;
                onSelect(next);
                setCurrentPage(1);
              }}
              className="appearance-none w-4 h-4 border border-gray-400 mr-2 rounded-sm checked:bg-pl-accent"
            />
            {item.label}
            <span className={commonClassNames.count}>{item.count}</span>
          </label>
        ))}
      </div>

      {!search.trim() && items.length > 10 && (
        <button
          onClick={() => setLimit(limit === 10 ? 20 : 10)}
          className={commonClassNames.showMore}
        >
          {limit === 10 ? "Show more" : "Show less"}
        </button>
      )}
    </div>
  );

  const filters = [
    {
      id: "series",
      title: "Filter by report",
      content: (
        <div className="flex flex-col space-y-1">
          {reportItems.map((item) => (
            <label
              key={item.value}
              className={`flex items-center text-13 mb-1 px-2 py-1 rounded ${
                item.value === selectedReport ? "dark:bg-[#333]" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={item.value === selectedReport}
                onChange={() => {
                  const nextReport = item.value === selectedReport ? "" : item.value;
                  setSelectedReport(nextReport);
                  setSelectedReportYear("");
                  setSelectedJudgmentYear("");
                  setSelectedVolume("");
                  setCurrentPage(1);
                }}
                className="w-4 h-4 mr-2 accent-pl-accent"
              />
              {item.label}
              <span className={commonClassNames.count}>{item.count}</span>
            </label>
          ))}
        </div>
      ),
    },
  ];

  if (selectedReport) {
    const reportYearFilter = {
      id: "report_year",
      title: "Filter by Sri LR year",
      content: renderYearFilterContent({
        items: reportYearItems,
        filteredItems: filteredReportYearItems,
        search: reportYearSearch,
        setSearch: setReportYearSearch,
        limit: reportYearLimit,
        setLimit: setReportYearLimit,
        selectedValue: selectedReportYear,
        onSelect: setSelectedReportYear,
        helperText:
          selectedReport === "Sri-LR"
            ? "Reports judgments published in the stated year, with a few from the prior year."
            : null,
      }),
    };

    const judgmentYearFilter = {
      id: "judgment_year",
      title: "Filter by judgment year",
      content: renderYearFilterContent({
        items: judgmentYearItems,
        filteredItems: filteredJudgmentYearItems,
        search: judgmentYearSearch,
        setSearch: setJudgmentYearSearch,
        limit: judgmentYearLimit,
        setLimit: setJudgmentYearLimit,
        selectedValue: selectedJudgmentYear,
        onSelect: setSelectedJudgmentYear,
        helperText:
          selectedReport === "NLR"
            ? "NLR volumes are numbered sequentially, not by year. The year here is derived from the judgment date."
            : null,
      }),
    };

    const volumeFilter = {
      id: "volume",
      title:
        selectedReport === "Sri-LR"
          ? isMobile
            ? "Filter by volume"
            : "Filter by volume (within year)"
          : selectedReport === "NLR"
            ? "Filter by series"
            : "Filter by volume",
      content: (
        <div className="flex flex-col items-start">
          {!isMobile && selectedReport === "Sri-LR" && (
            <p className="mb-2 text-11 leading-4 text-pl-text-tertiary">
              Sri LRs are published in 1-3 volumes for a given year.
            </p>
          )}
          {!isMobile && selectedReport === "NLR" && (
            <p className="mb-2 text-11 leading-4 text-pl-text-tertiary">
              NLR volumes are numbered sequentially across the series and cover 1895–1978, with a few earlier judgments.
            </p>
          )}
          <select
            value={selectedVolume}
            onChange={(event) => {
              const nextVolume = event.target.value;
              setSelectedVolume(nextVolume);
              setCurrentPage(1);
            }}
            style={{
              fontSize: "12px",
              color: "#888888",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "4px 8px",
              appearance: "auto",
              outline: "none",
              boxShadow: "none",
            }}
          >
            <option value="">
              {selectedReport === "NLR" ? "All series" : "All volumes"}
            </option>
            {sortedVolumeItems.map((item) => (
              <option key={item.value} value={item.value}>
                {formatVolumeLabel(item.label)}
              </option>
            ))}
          </select>
        </div>
      ),
    };

    if (selectedReport === "NLR") {
      filters.push(volumeFilter, judgmentYearFilter);
    } else {
      filters.push(reportYearFilter, volumeFilter, judgmentYearFilter);
    }
  }

  return (
    <div
      className={`pl-filter flex flex-col w-full text-pl-text-tertiary h-fit ${
        isMobile
          ? ""
          : "bg-pl-bg-elevated overflow-hidden border border-pl-border rounded-lg"
      }`}
    >
      {filters.map((filter) => (
        <div key={filter.title} className="border-b border-pl-border">
          <div
            onClick={() => togglePanel(filter.id)}
            className="p-4 cursor-pointer flex justify-between items-center text-14 font-medium hover:bg-pl-bg-subtle"
          >
            {filter.title}
            <span>{expandedPanel === filter.id ? "-" : "+"}</span>
          </div>

          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              expandedPanel === filter.id ? "max-h-[1000px]" : "max-h-0"
            }`}
          >
            <div className="p-4">{filter.content}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsContent({
  isDesktop,
  toggleFilters,
  authState,
  signIn,
  openPDFWithPreSignedUrl,
  pdfLoading,
  hits,
  facets,
  loading,
  error,
  currentPage,
  totalPages,
  onProtectedPageRequest,
  selectedReport,
  selectedReportYear,
  selectedJudgmentYear,
  selectedVolume,
  reportFacetItems,
  setSelectedReport,
  setSelectedReportYear,
  setSelectedJudgmentYear,
  setSelectedVolume,
  setCurrentPage,
}) {
  return (
    <main className="xl:ml-[220px] mb-40 flex-grow">
      <div className="flex flex-col py-20 pt-[150px] md:pt-[130px] max-w-6xl w-full px-3 md:px-0">
        <ActiveReportFilters
          selectedReport={selectedReport}
          selectedReportYear={selectedReportYear}
          selectedJudgmentYear={selectedJudgmentYear}
          selectedVolume={selectedVolume}
          setSelectedReport={setSelectedReport}
          setSelectedReportYear={setSelectedReportYear}
          setSelectedJudgmentYear={setSelectedJudgmentYear}
          setSelectedVolume={setSelectedVolume}
          setCurrentPage={setCurrentPage}
        />

        {!isDesktop && (
          <div className="mb-2">
            <div className="flex justify-end py-0.5">
              <button onClick={toggleFilters} className="md:hidden flex text-pl-accent">
                <FiFilter className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="flex w-full md:gap-50">
          <div className="md:w-3/4 w-full flex flex-col justify-between h-full">
            <div className="flex-1">
              <div className="w-full max-w-[820px]">
                <div className="relative bg-pl-bg-elevated border border-pl-border rounded-xl text-14 text-left px-[10px] md:px-4">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="border-b border-pl-border text-left text-15 md:text-16 font-semibold text-pl-text-primary">
                        <th className="py-3 md:py-4 pl-2 pr-2 w-[88px] md:w-[170px] whitespace-nowrap text-left">
                          {selectedReport === "Sri-LR"
                            ? "Sri-LR No."
                            : selectedReport === "NLR"
                              ? "NLR No."
                              : "Report No."}
                        </th>
                        <th className="py-3 md:py-4 px-2 truncate text-left">
                          Parties
                        </th>
                        <th
                          className="py-3 md:py-4 pr-2 pl-2 w-[76px] md:w-[120px]"
                          aria-label="Summary actions"
                        />
                      </tr>
                    </thead>

                    <tbody>
                      {hits.length > 0 ? (
                        <CustomHits
                          hits={hits}
                          selectedReport={selectedReport}
                          authState={authState}
                          signIn={signIn}
                          openPDFWithPreSignedUrl={openPDFWithPreSignedUrl}
                          pdfLoading={pdfLoading}
                        />
                      ) : (
                        <tr>
                          <td colSpan="3" className="px-6 py-6 text-center text-pl-text-tertiary">
                            {loading ? "Loading law reports..." : error || "No matching results found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-50">
              <ReportPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                isAuthenticated={authState.isAuthenticated}
                onAuthRequired={onProtectedPageRequest}
              />
            </div>
          </div>

          {isDesktop && (
            <div className="flex w-1/4 px-10">
              <ReportFiltersPanel
                facets={facets}
                selectedReport={selectedReport}
                selectedReportYear={selectedReportYear}
                selectedJudgmentYear={selectedJudgmentYear}
                selectedVolume={selectedVolume}
                reportFacetItems={reportFacetItems}
                setSelectedReport={setSelectedReport}
                setSelectedReportYear={setSelectedReportYear}
                setSelectedJudgmentYear={setSelectedJudgmentYear}
                setSelectedVolume={setSelectedVolume}
                setCurrentPage={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

const Reports = () => {
  const {
    state,
    signIn,
    getAccessToken,
    refreshAccessToken,
    revokeAccessToken,
  } = useAuthContext();
  const navigate = useNavigate();
  const storedReportState = useMemo(
    () =>
      normalizeReportState(
        getStoredReportState(PENDING_REPORT_STATE_KEY) ||
          getStoredReportState(REPORT_STATE_KEY) ||
          {}
      ),
    []
  );
  const [searchQuery, setSearchQuery] = useState(storedReportState.searchQuery);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [userToken, setUserToken] = useState("noToken");
  const [forceSignedOut, setForceSignedOut] = useState(false);

  const [hits, setHits] = useState([]);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(storedReportState.page);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReport, setSelectedReport] = useState(storedReportState.selectedReport);
  const [selectedReportYear, setSelectedReportYear] = useState(storedReportState.selectedReportYear);
  const [selectedJudgmentYear, setSelectedJudgmentYear] = useState(
    storedReportState.selectedJudgmentYear
  );
  const [selectedVolume, setSelectedVolume] = useState(storedReportState.selectedVolume);
  const [reportFacetItems, setReportFacetItems] = useState(storedReportState.reportFacetItems);
  const didRunPageResetEffectRef = useRef(false);
  const skipNextPageResetRef = useRef(false);
  const latestRequestIdRef = useRef(0);
  const activeReportsRequestRef = useRef(null);
  const didRestorePendingStateRef = useRef(false);

  const presignedBaseUrl = import.meta.env.VITE_PRESIGNED_BASE_URL;
  const reportsService = useMemo(() => ReportsService.getInstance(), []);
  const effectiveAuthState = useMemo(
    () => ({
      ...state,
      isAuthenticated: state.isAuthenticated && !forceSignedOut,
    }),
    [forceSignedOut, state]
  );

  window.dataLayer = window.dataLayer || [];

  useEffect(() => {
    const fetchToken = async () => {
      if (state.isAuthenticated) {
        try {
          const accessToken = await getAccessToken();
          setForceSignedOut(false);
          setUserToken(accessToken);
        } catch (tokenError) {
          console.error("Failed to refresh reports token:", tokenError);
          await handleExpiredReportSession();
        }
      } else {
        setForceSignedOut(false);
        setUserToken("noToken");
      }
    };

    fetchToken();
  }, [getAccessToken, state.isAuthenticated]);

  const handleExpiredReportSession = async () => {
    try {
      await revokeAccessToken();
    } catch (revokeError) {
      console.error("Failed to revoke expired reports token:", revokeError);
    } finally {
      setForceSignedOut(true);
      setUserToken("noToken");
      sessionStorage.removeItem(PENDING_REPORT_STATE_KEY);
      sessionStorage.removeItem("redirectAfterLogin");
      sessionStorage.removeItem("urlString");
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openPDFWithPreSignedUrl = async (originalLink) => {
    setPdfLoading(true);

    const newWindow = window.open("about:blank", "_blank");

    try {
      if (!presignedBaseUrl) {
        if (newWindow) {
          newWindow.location.href = originalLink;
        }
        return;
      }

      const request = (accessToken) =>
        fetch(`${presignedBaseUrl}/api/pdf/get-pdf-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ link: originalLink }),
        });

      let accessToken = state.isAuthenticated
        ? (await getAccessToken()) || ""
        : "";
      let response = await request(accessToken);
      if (response.status === 401 && state.isAuthenticated) {
        await refreshAccessToken();
        accessToken = (await getAccessToken()) || "";
        response = await request(accessToken);
      }

      const data = await response.json();

      if (data.success && newWindow) {
        newWindow.location.href = data.presigned_url;

        window.dataLayer.push({
          event: "view_pdf",
          event_category: "PDF",
          event_label: "View PDF",
          value: originalLink,
        });
      } else if (newWindow) {
        console.error("Error getting PDF URL:", data.error);
        newWindow.location.href = originalLink;
      }
    } catch (requestError) {
      console.error("Error fetching pre-signed URL:", requestError);
      if (newWindow) {
        newWindow.location.href = originalLink;
      }
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    const redirectToDocument = sessionStorage.getItem("redirectToDocument");
    if (redirectToDocument && state.isAuthenticated) {
      openPDFWithPreSignedUrl(redirectToDocument);
      sessionStorage.removeItem("redirectToDocument");
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    document.body.style.overflow = isFilterOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFilterOpen]);

  const getCurrentReportState = (page = currentPage) => ({
    page,
    searchQuery,
    selectedReport,
    selectedReportYear,
    selectedJudgmentYear,
    selectedVolume,
    reportFacetItems,
  });

  const saveReportState = (key, page = currentPage) => {
    sessionStorage.setItem(key, JSON.stringify(getCurrentReportState(page)));
  };

  const handleProtectedPageRequest = (page) => {
    saveReportState(PENDING_REPORT_STATE_KEY, page);
    saveReportState(REPORT_STATE_KEY, page);
    sessionStorage.removeItem("urlString");
    sessionStorage.setItem("redirectAfterLogin", window.location.pathname);
    signIn();
  };

  const loadReports = async (targetPage = currentPage) => {
    activeReportsRequestRef.current?.abort();
    const controller = new AbortController();
    activeReportsRequestRef.current = controller;
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setLoading(true);
    setError("");

    try {
      const data = await reportsService.searchReports({
        query: searchQuery,
        page: targetPage,
        report: selectedReport,
        reportYear: selectedReportYear,
        judgmentYear: selectedJudgmentYear,
        volume: selectedVolume,
        token: userToken !== "noToken" ? userToken : "",
        isAuthenticated: state.isAuthenticated && !forceSignedOut,
        getAccessToken,
        refreshAccessToken,
        signal: controller.signal,
      });

      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setHits(data.hits || []);
      setFacets(data.facets || {});
      if (!selectedReport && data.facets?.report?.length) {
        const nextReportFacetItems = toFacetItems(data.facets.report);
        setReportFacetItems(nextReportFacetItems);
        sessionStorage.setItem(
          REPORT_STATE_KEY,
          JSON.stringify({
            ...getCurrentReportState(data.page || targetPage),
            reportFacetItems: nextReportFacetItems,
          })
        );
      }
      setCurrentPage(data.page || targetPage);
      setTotalPages(data.total_pages || 1);
    } catch (requestError) {
      if (requestError.name === "AbortError") {
        return;
      }
      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      if (requestError.message === "AUTH_REQUIRED") {
        await handleExpiredReportSession();
        return;
      }

      setError(requestError.message || "Failed to load reports");
      setHits([]);
      setTotalPages(1);
    } finally {
      if (activeReportsRequestRef.current === controller) {
        activeReportsRequestRef.current = null;
      }
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (
      state.isLoading ||
      (state.isAuthenticated && !forceSignedOut && userToken === "noToken")
    ) {
      return;
    }

    const timer = window.setTimeout(
      () => loadReports(currentPage),
      REPORT_SEARCH_DEBOUNCE_MS
    );

    return () => {
      window.clearTimeout(timer);
      activeReportsRequestRef.current?.abort();
    };
  }, [
    currentPage,
    searchQuery,
    selectedReport,
    selectedReportYear,
    selectedJudgmentYear,
    selectedVolume,
    forceSignedOut,
    state.isAuthenticated,
    state.isLoading,
    userToken,
  ]);

  useEffect(() => {
    saveReportState(REPORT_STATE_KEY, currentPage);
  }, [
    currentPage,
    searchQuery,
    reportFacetItems,
    selectedReport,
    selectedVolume,
    selectedReportYear,
    selectedJudgmentYear,
  ]);

  useEffect(() => {
    if (
      didRestorePendingStateRef.current ||
      state.isLoading ||
      !state.isAuthenticated ||
      forceSignedOut ||
      userToken === "noToken"
    ) {
      return;
    }

    const pendingReportState = getStoredReportState(PENDING_REPORT_STATE_KEY);

    if (!pendingReportState) {
      return;
    }

    const restoredReportState = normalizeReportState(pendingReportState);
    didRestorePendingStateRef.current = true;
    skipNextPageResetRef.current = true;
    setSearchQuery(restoredReportState.searchQuery);
    setSelectedReport(restoredReportState.selectedReport);
    setSelectedReportYear(restoredReportState.selectedReportYear);
    setSelectedJudgmentYear(restoredReportState.selectedJudgmentYear);
    setSelectedVolume(restoredReportState.selectedVolume);
    setReportFacetItems(restoredReportState.reportFacetItems);
    setCurrentPage(restoredReportState.page);
    sessionStorage.removeItem(PENDING_REPORT_STATE_KEY);
    sessionStorage.removeItem("redirectAfterLogin");
  }, [forceSignedOut, state.isAuthenticated, state.isLoading, userToken]);

  useEffect(() => {
    if (state.isLoading || !state.isAuthenticated || forceSignedOut || userToken === "noToken") {
      return;
    }

    const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");

    if (redirectAfterLogin?.startsWith("/summary/")) {
      sessionStorage.removeItem("redirectAfterLogin");
      navigate(redirectAfterLogin);
      return;
    }

    if (redirectAfterLogin === window.location.pathname) {
      sessionStorage.removeItem("redirectAfterLogin");
    }
  }, [forceSignedOut, navigate, state.isAuthenticated, state.isLoading, userToken]);

  useEffect(() => {
    if (!didRunPageResetEffectRef.current) {
      didRunPageResetEffectRef.current = true;
      return;
    }

    if (skipNextPageResetRef.current) {
      skipNextPageResetRef.current = false;
      return;
    }

    setCurrentPage(1);
  }, [searchQuery, selectedReport, selectedReportYear, selectedJudgmentYear, selectedVolume]);

  const toggleFilters = () => setIsFilterOpen((prev) => !prev);

  return (
    <div className="min-h-screen flex flex-col bg-pl-bg">
      <Helmet>
        <title>Sri Lanka Law Reports | paralegal.lk</title>
        <meta
          name="description"
          content="Search and browse the full list of Sri Lanka Law Reports with filtering and pagination."
        />
      </Helmet>

      <ReportHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        forceSignedOut={forceSignedOut}
      />

      <ReportsContent
        isDesktop={isDesktop}
        toggleFilters={toggleFilters}
        authState={effectiveAuthState}
        signIn={signIn}
        openPDFWithPreSignedUrl={openPDFWithPreSignedUrl}
        pdfLoading={pdfLoading}
        hits={hits}
        facets={facets}
        loading={loading}
        error={error}
        currentPage={currentPage}
        totalPages={totalPages}
        onProtectedPageRequest={handleProtectedPageRequest}
        selectedReport={selectedReport}
        selectedReportYear={selectedReportYear}
        selectedJudgmentYear={selectedJudgmentYear}
        selectedVolume={selectedVolume}
        reportFacetItems={reportFacetItems}
        setSelectedReport={setSelectedReport}
        setSelectedReportYear={setSelectedReportYear}
        setSelectedJudgmentYear={setSelectedJudgmentYear}
        setSelectedVolume={setSelectedVolume}
        setCurrentPage={setCurrentPage}
      />

      {!isDesktop && (
        <div
          className={`fixed inset-0 z-[149] bg-black bg-opacity-30 dark:bg-opacity-50 transition-opacity duration-300 ${
            isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
          onClick={toggleFilters}
        >
          <div
            className={`fixed top-0 pb-50 right-0 w-[260px] max-w-[85vw] h-full bg-pl-bg z-[150] shadow-lg transform transition-transform duration-300 border-l border-pl-border ${
              isFilterOpen ? "translate-x-0" : "translate-x-full"
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex p-10 pr-20 justify-between items-center bg-pl-bg-elevated border-y border-pl-border">
              <div className="text-16 text-pl-text-secondary font-medium">Filters</div>
              <button
                className="text-12 px-15 py-0.5 rounded-25 bg-pl-accent text-white border border-pl-accent"
                onClick={toggleFilters}
              >
                Save
              </button>
            </div>
            <div className="h-full overflow-y-auto pb-24">
              <ReportFiltersPanel
                isMobile
                facets={facets}
                selectedReport={selectedReport}
                selectedReportYear={selectedReportYear}
                selectedJudgmentYear={selectedJudgmentYear}
                selectedVolume={selectedVolume}
                reportFacetItems={reportFacetItems}
                setSelectedReport={setSelectedReport}
                setSelectedReportYear={setSelectedReportYear}
                setSelectedJudgmentYear={setSelectedJudgmentYear}
                setSelectedVolume={setSelectedVolume}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Reports;
